import dayjs from "dayjs";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import { DashboardBucket, FiscalPeriod } from "../../repository/data.default";

// The DRY core of the company analytics: a range model that resolves to a concrete date window, a query builder,
// and the aggregation strategies. Every chart consumes a ResolvedRange (or a period set) — they stay range-agnostic,
// so future metrics (loyalty/engagement/KPI) and future modes plug in here only.

export type RangeMode = "all" | "preset" | "fiscal" | "custom";
export type RangePreset = "30d" | "60d" | "quarter" | "semester" | "year";

export interface AnalyticsRange {
  mode: RangeMode;
  preset?: RangePreset;
  fiscalPeriodUid?: string;
  custom?: { start: string; end: string };
}

export interface ResolvedRange {
  // Full datetimes (offset format, matching the backend DateTimeOffset parser); null => unbounded (All time).
  start: string | null;
  end: string | null;
  label: string;
}

// Start-of-day / end-of-day are essential: the backend Between is inclusive and a bare date parses to midnight,
// so an end bound must be end-of-day to capture that day's records.
const startOfDay = (d: any) => dayjs(d).startOf("day").format("YYYY-MM-DDTHH:mm:ssZ");
const endOfDay = (d: any) => dayjs(d).endOf("day").format("YYYY-MM-DDTHH:mm:ssZ");

// Pick a dashboard trend's time bucket from a resolved date span so the charts stay readable:
// unbounded (all-time) and long spans roll up to months, medium spans to weeks, short spans stay daily.
export const pickBucket = (from?: string, to?: string): DashboardBucket => {
  if (!from || !to) return "month";
  const spanDays = dayjs(to).diff(dayjs(from), "day");
  if (spanDays > 120) return "month";
  if (spanDays > 45) return "week";
  return "day";
};

export function resolveRange(range: AnalyticsRange, fiscalPeriods: FiscalPeriod[]): ResolvedRange {
  switch (range.mode) {
    case "all":
      return { start: null, end: null, label: "All time" };
    case "preset": {
      const now = dayjs();
      let start = now;
      let label = "Last 60 days";
      switch (range.preset) {
        case "30d": start = now.subtract(29, "day"); label = "Last 30 days"; break;
        case "60d": start = now.subtract(59, "day"); label = "Last 60 days"; break;
        case "quarter": start = now.subtract(3, "month"); label = "Last quarter"; break;
        case "semester": start = now.subtract(6, "month"); label = "Last semester"; break;
        case "year": start = now.startOf("year"); label = "This year"; break;
      }
      return { start: startOfDay(start), end: endOfDay(now), label };
    }
    case "fiscal": {
      const p = fiscalPeriods.find((x) => x.uid === range.fiscalPeriodUid);
      if (!p) return { start: null, end: null, label: "All time" };
      return { start: startOfDay(p.startDate), end: endOfDay(p.endDate), label: p.name };
    }
    case "custom":
      if (!range.custom) return { start: null, end: null, label: "All time" };
      return {
        start: startOfDay(range.custom.start),
        end: endOfDay(range.custom.end),
        label: `${range.custom.start} – ${range.custom.end}`,
      };
  }
}

export function buildCompanyMetricQuery(
  companyUid: string,
  dateField: string,
  resolved: ResolvedRange,
): SearchQuery {
  const conditions: SearchQuery["conditions"] = [
    { field: "companyUid", operation: QueryOperation.Equal, values: [companyUid] },
  ];
  if (resolved.start && resolved.end) {
    conditions.push({
      field: dateField,
      operation: QueryOperation.Between,
      values: [resolved.start, resolved.end],
    });
  }
  return { conditions };
}

// ---- Aggregation strategies ----

// Trend: count records per day -> { [YYYY-MM-DD]: count } (line chart input). Same shape as the original logins reduce.
export function bucketByDay(records: any[], dateField: string): { [day: string]: number } {
  return records.reduce((acc: { [k: string]: number }, item) => {
    const day = dayjs(item[dateField]).format("YYYY-MM-DD");
    acc[day] = (acc[day] ?? 0) + 1;
    return acc;
  }, {});
}

// Breakdown: group records by a categorical field -> [{ type, value }] (pie/ring/column input).
export function groupByField(records: any[], key: string): { type: string; value: number }[] {
  return records.reduce((acc: { type: string; value: number }[], item) => {
    const type = item[key] ?? "—";
    const existing = acc.find((e) => e.type === type);
    if (existing) existing.value++;
    else acc.push({ type, value: 1 });
    return acc;
  }, []);
}

// Period comparison: count records falling inside each fiscal period's [StartDate, EndDate] -> [{ type: name, value }]
// (column input). Periods are ordered by StartDate so quarters/semesters read left-to-right.
export function bucketByFiscalPeriods(
  records: any[],
  dateField: string,
  periods: FiscalPeriod[],
): { type: string; value: number }[] {
  const ordered = [...periods].sort((a, b) => a.startDate.localeCompare(b.startDate));
  return ordered.map((p) => {
    const start = dayjs(p.startDate).startOf("day").valueOf();
    const end = dayjs(p.endDate).endOf("day").valueOf();
    const value = records.filter((r) => {
      const t = dayjs(r[dateField]).valueOf();
      return t >= start && t <= end;
    }).length;
    return { type: p.name, value };
  });
}

// Period averaging: mean of a numeric field over records inside each fiscal period -> [{ type: name, value }].
// Unlike bucketByFiscalPeriods (counts), this is for value metrics like KPI scores. Periods with no records => 0.
export function averageByFiscalPeriods(
  records: any[],
  valueField: string,
  dateField: string,
  periods: FiscalPeriod[],
): { type: string; value: number }[] {
  const ordered = [...periods].sort((a, b) => a.startDate.localeCompare(b.startDate));
  return ordered.map((p) => {
    const start = dayjs(p.startDate).startOf("day").valueOf();
    const end = dayjs(p.endDate).endOf("day").valueOf();
    const inPeriod = records.filter((r) => {
      const t = dayjs(r[dateField]).valueOf();
      return t >= start && t <= end;
    });
    const value = inPeriod.length
      ? inPeriod.reduce((s, r) => s + Number(r[valueField] ?? 0), 0) / inPeriod.length
      : 0;
    return { type: p.name, value: Math.round(value * 100) / 100 };
  });
}
